import { useEffect, useMemo, useState } from 'react'
import { Download, FileText, Printer } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { AccountingSections, Section } from './accounting-sections'
import { CloseList } from './close-list'
import { ClemensView } from './clemens-view'
import { KontrolleLine, OpenLine } from './period-status'
import type {
  AccountingMode,
  AccountingSearch,
  AccountingSelection,
  Ansicht,
} from '@/features/reports/lib/accounting-period'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { PermissionGate } from '@/shared/components/permission-gate'
import { ACTIONS } from '@/shared/constants/actions'
import { useLocationStore } from '@/shared/store/location-store'
import { downloadAuthenticated } from '@/shared/lib/download'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/features/auth/store/auth-store'
import { useRestaurants } from '@/features/products/hooks/use-restaurants'
import { ReportFilterBar } from '@/features/reports/components/report-filter-bar'
import { AccountingLocationPicker } from '@/features/reports/components/accounting-location-picker'
import {
  AccountingPeriodControl,
  AnsichtSwitch,
  windowLine,
} from '@/features/reports/components/accounting-period-bar'
import {
  buildPeriodParams,
  currentMonth,
  monthOf,
  searchOf,
  selectionOf,
} from '@/features/reports/lib/accounting-period'
import {
  hasSheet,
  useClosingPeriodQuery,
} from '@/features/reports/api/use-closing-report-query'
import {
  accountingUserKey,
  useAccountingAnsichtStore,
} from '@/features/reports/store/accounting-ansicht-store'
import { formatDatetime } from '@/features/reports/lib/format'

/**
 * Buchhaltung — the controller's working document, by Tagesabschluss
 * (CHG-2026-09-tax-agency-report, CHG-2026-10-accounting-close-periods).
 *
 * One page per Standort and close-bounded period, in two Ansichten of one response: the
 * **Übersicht** (UMSATZ per VAT rate, SPARTEN, KANÄLE, ZAHLUNGEN an der Kassa / online,
 * unentgeltliche Abgaben, Trinkgeld, and the list of the period's Tagesabschlüsse) and
 * **Clemens**, the controller's monthly sheet with one row per close. Period and Ansicht live in
 * the URL (D7); when the URL names no Ansicht, the one this user had last in this browser opens.
 *
 * The reader files from it, so the page says what it is — period and Standort in the header — and
 * carries no advice and no banners: the Kontrolle is one line, the open amount another.
 */
export function AccountingPage({
  search,
  onNavigate,
}: {
  search: AccountingSearch
  onNavigate: (search: AccountingSearch) => void
}) {
  return (
    <PermissionGate action={ACTIONS.REPORT_ACCOUNTING_VIEW}>
      <AccountingContent search={search} onNavigate={onNavigate} />
    </PermissionGate>
  )
}

function AccountingContent({
  search,
  onNavigate,
}: {
  search: AccountingSearch
  onNavigate: (search: AccountingSearch) => void
}) {
  const { t } = useTranslation('reports')
  const now = useMemo(() => new Date(), [])
  const thisMonth = currentMonth(now)

  const { data: restaurantData } = useRestaurants()
  // Every Standort, the internal staff store included: here it is a normal location the controller
  // decides how to treat, which is exactly what the shared LocationSwitcher must not offer.
  const restaurants = restaurantData?.items ?? []
  // The global store holds a LIST and means "alle Standorte" when empty — neither of which this
  // report can use, because a filing is per Standort. Its first entry seeds the selection so that
  // arriving from the dashboard lands on the store the reader was already looking at; from there
  // this page owns its own single selection.
  const storedIds = useLocationStore((s) => s.restaurantIds)
  const [restaurantId, setRestaurantId] = useState<number | null>(null)
  useEffect(() => {
    if (restaurantId != null || restaurants.length === 0) return
    const seeded = restaurants.find((r) => r.id === storedIds[0])
    setRestaurantId(seeded?.id ?? restaurants[0].id)
  }, [restaurantId, restaurants, storedIds])

  // The Ansicht: the URL's, else this user's last one in this browser, else the Übersicht.
  const userKey = accountingUserKey(useAuthStore((s) => s.user))
  const remembered = useAccountingAnsichtStore((s) => s.byUser[userKey])
  const rememberAnsicht = useAccountingAnsichtStore((s) => s.setAnsicht)
  const ansicht: Ansicht = search.ansicht ?? remembered ?? 'uebersicht'
  useEffect(() => {
    if (search.ansicht && userKey) rememberAnsicht(userKey, search.ansicht)
  }, [search.ansicht, userKey, rememberAnsicht])

  const selection = selectionOf(search, now)
  // The mode on screen may run ahead of the URL while a Tag or Zeitraum is still being picked; only
  // a whole selection is asked for.
  const [mode, setMode] = useState<AccountingMode>(selection.mode)
  useEffect(() => setMode(selection.mode), [selection.mode])
  const complete = mode === selection.mode
  const params = complete ? buildPeriodParams(selection, restaurantId) : null
  const report = useClosingPeriodQuery(params)
  const data = complete ? report.data : undefined

  const go = (next: AccountingSelection) =>
    onNavigate(searchOf(next, search.ansicht))
  // Clicking a close selects its Tag and keeps the Ansicht (D7).
  const selectClose = (ref: string) => go({ mode: 'tag', close: ref })

  // The pickers open where the period on screen is, also while a new one is being picked.
  const shown = report.data
  const viewMonth =
    selection.mode === 'monat'
      ? selection.month
      : shown?.period.to
        ? monthOf(shown.period.to)
        : thisMonth
  const fromMonth = shown?.period.from ? monthOf(shown.period.from) : viewMonth

  const changeMode = (next: AccountingMode) => {
    // Set first: a selection that is already in the URL does not change it, and the mode on screen
    // still has to follow the click.
    setMode(next)
    const closes = shown?.closes ?? []
    if (next === 'monat') return go({ mode: 'monat', month: viewMonth })
    // A Tag opens on the period's last close, a Zeitraum on the period's closes; with nothing to
    // open on, the pickers wait for a choice.
    if (next === 'tag' && closes.length > 0) {
      return go({ mode: 'tag', close: closes[closes.length - 1].ref })
    }
    if (next === 'zeitraum' && closes.length > 1) {
      return go({
        mode: 'zeitraum',
        after: closes[0].ref,
        until: closes[closes.length - 1].ref,
      })
    }
  }

  const changeAnsicht = (next: Ansicht) => {
    if (userKey) rememberAnsicht(userKey, next)
    onNavigate(searchOf(selection, next))
  }

  const restaurantName =
    data?.period.restaurantName ??
    restaurants.find((r) => r.id === restaurantId)?.name ??
    ''
  const headline = t(`accounting.headline.${mode}`)
  const line = data ? windowLine(t, data.period) : ''

  const exportFile = (format: 'csv' | 'pdf') => {
    if (!params) return
    const query = format === 'csv' ? `${params}&ansicht=${ansicht}` : params
    void downloadAuthenticated(
      `/api/v1/admin/reports/closing/period/export.${format}?${query}`,
      `${headline}${format === 'csv' && ansicht === 'clemens' ? '-Clemens' : ''}.${format}`,
    )
  }

  return (
    // A closing sheet is a document, and a document has a measure: 1280px is the canvas's own sheet
    // width and the widest measure two money columns still scan across. Ansicht Clemens is a wide
    // table of 16 money columns, so it takes the whole width the screen offers and scrolls only
    // when even that is too narrow.
    <div
      className={cn(
        'mx-auto w-full space-y-4',
        ansicht === 'clemens' ? 'max-w-none' : 'max-w-7xl',
      )}
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          {/* The headline follows the period: TAGESABSCHLUSS for one close, MONATSBERICHT and
              ZEITRAUMBERICHT for a run of them — as the PDF prints it. */}
          <h1 className="text-2xl font-bold tracking-tight">{headline}</h1>
          <p className="text-sm text-muted-foreground">
            {[line, restaurantName].filter(Boolean).join(' · ')}
          </p>
        </div>
        <div
          className="hidden flex-wrap items-center gap-2 sm:flex"
          data-print-hide
        >
          <Button variant="outline" size="sm" onClick={() => window.print()}>
            <Printer className="mr-1 h-4 w-4" />
            {t('print')}
          </Button>
          {/* Both exports are rendered by the server from the same computation as the page: the
              CSV follows the Ansicht, the PDF is always the printable Übersicht (D8). */}
          <Button
            variant="outline"
            size="sm"
            disabled={!params}
            onClick={() => exportFile('csv')}
          >
            <Download className="mr-1 h-4 w-4" />
            {t('accounting.exportCsv')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!params}
            onClick={() => exportFile('pdf')}
          >
            <FileText className="mr-1 h-4 w-4" />
            {t('accounting.exportPdf')}
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <ReportFilterBar
          summary={[t(`accounting.modes.${mode}`), line, restaurantName]
            .filter(Boolean)
            .join(' · ')}
        >
          <AccountingPeriodControl
            mode={mode}
            selection={selection}
            restaurantId={restaurantId}
            viewMonth={viewMonth}
            fromMonth={fromMonth}
            thisMonth={thisMonth}
            onModeChange={changeMode}
            onSelect={go}
          />
          <AccountingLocationPicker
            restaurants={restaurants}
            value={restaurantId}
            onChange={(id) => {
              setRestaurantId(id)
              // A close belongs to the Standort it was made at; another Standort opens on the month.
              if (selection.mode !== 'monat')
                go({ mode: 'monat', month: viewMonth })
            }}
          />
        </ReportFilterBar>
        <AnsichtSwitch value={ansicht} onChange={changeAnsicht} />
      </div>

      {data?.open ? <OpenLine open={data.open} /> : null}

      {report.error && complete ? (
        <Alert variant="destructive">
          <AlertTitle>{t('errorTitle')}</AlertTitle>
          <AlertDescription>{t('errorDescription')}</AlertDescription>
        </Alert>
      ) : null}

      {complete && report.isLoading && params ? (
        <Skeleton className="h-96 w-full" />
      ) : null}

      {!complete ? (
        <p className="text-sm text-muted-foreground">
          {t(mode === 'tag' ? 'accounting.pickTag' : 'accounting.pickZeitraum')}
        </p>
      ) : null}

      <div
        className={
          report.isFetching && !report.isLoading
            ? 'space-y-4 opacity-60 transition-opacity'
            : 'space-y-4'
        }
      >
        {data && !hasSheet(data) ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {t(
              data.open && data.open.sinceClose == null
                ? 'accounting.empty.never'
                : 'accounting.empty.period',
            )}
          </p>
        ) : null}
        {data && hasSheet(data) && ansicht === 'uebersicht' ? (
          <>
            <AccountingSections report={data} />
            <CloseList closes={data.closes} onSelect={selectClose} />
            <Section title={t('accounting.kontrolle.title')}>
              <KontrolleLine
                kontrolle={data.kontrolle}
                onSelect={selectClose}
                className="px-4 py-2"
              />
            </Section>
          </>
        ) : null}
        {data && hasSheet(data) && ansicht === 'clemens' ? (
          <ClemensView report={data} onSelect={selectClose} />
        ) : null}
      </div>

      {data?.lastSync ? (
        <p className="text-xs text-muted-foreground">
          {t('accounting.lastSync', { at: formatDatetime(data.lastSync) })}
        </p>
      ) : null}

      {restaurantId == null && !report.isLoading ? (
        <p className="text-sm text-muted-foreground">
          {t('accounting.pickLocation')}
        </p>
      ) : null}
    </div>
  )
}
